import { Component, OnInit, Input, ViewChild, Inject, EventEmitter, Output, SimpleChange } from '@angular/core';
import { Subscription } from 'rxjs';

import { ErrorService } from './services/error/error.service';
import { WizardService } from './services/wizard/wizard.service';
import { EventService } from './services/event/event.service';
import { WizardBuilderService } from './services/wizard-builder/wizard-builder.service';

import { FormItem } from './classes/form-item';
import { FormDirective } from './directives/form/form.directive';

import {
    SingleNamePipe,
    IWizardConfig,
    IWizardPage,
    IWizardMetaData,
    IWizardStepMetaData,
    IWizardSkill,
    IWizardStep,
    IWizardAction,
    WizardActionTrackingEvent,
    WizardNextPage,
    IWizardUser,
    UserPlan,
    IDefaultWizardOptions
} from 'common-lib';

interface WizardConfigChange<T> extends SimpleChange {
    previousValue: T;
    currentValue: T;
}

import * as _ from 'lodash';
@Component({
    selector: 'calendarhero-wizard',
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.scss'],
    providers: [SingleNamePipe, WizardService, EventService, ErrorService]
})
export class WizardComponent implements OnInit {
    @Input() wizardTask?: IWizardSkill;
    @Input() wizardMetaData?: IWizardMetaData;
    @Input() wizardConfig: IWizardConfig = {
        taskId: '',
        skill: 'meeting-create',
        isPlugin: false,
        selectedMeetingType: '',
        componentsToHide: [],
        isScheduled: false
    };
    @Input() meeting?: IDefaultWizardOptions;
    @Input() meetingTypes = ['meeting'];
    @Input() wizardUser?: IWizardUser = {
        name: '',
        avatar: 'Ava',
        plan: UserPlan.Basic,
        timezone: 'America/Toronto'
    };
    @Output() onHandleCancel = new EventEmitter<WizardNextPage>();
    @Output() onHandleTrackEvent = new EventEmitter<WizardActionTrackingEvent>();
    @ViewChild(FormDirective, { static: true }) formHost: FormDirective;

    private pages: {
        [key: string]: IWizardPage;
    } = {};
    public currentPage?: IWizardPage;
    public actions?: IWizardAction[];
    public steps?: IWizardStep[];
    public currentStep = 1;

    private formItems: FormItem[] = [];
    public containerClasses: string[] = [''];
    public stepsMetaData: IWizardStepMetaData = { appName: '', selection: [] };

    private pageSubscription: Subscription;
    private selectionSubscription: Subscription;

    constructor(
        public errorService: ErrorService,
        public eventService: EventService,
        private wizardService: WizardService,
        private wizardBuilder: WizardBuilderService,
        @Inject(SingleNamePipe) private singleNamePipe: SingleNamePipe
    ) {}

    ngOnInit() {
        this.initWizardConfig(this.wizardConfig);
        this.initWizardUser(this.wizardUser);
        this.initMeetingTypes(this.meetingTypes);
        this.initNavigation();
        this.initSubscriptions();
        this.initExistingMeeting(this.meeting);
        this.checkHasCalendarProviders();
        this.buildForm();
        this.setMetaData();
        this.checkIsBasicallyRestricted();
    }

    ngOnDestroy() {
        this.pageSubscription.unsubscribe();
        this.selectionSubscription.unsubscribe();
    }

    private initNavigation() {
        this.currentStep = 1;
        this.steps = this.wizardTask.steps;
        this.pages = this.wizardTask.pages;
        this.currentPage = _.find(this.pages, (page) => {
            return page.step === 1;
        });
        this.actions = this.currentPage.actions;
        this.eventService.firstPage = Object.keys(this.pages)[0];
    }

    private initWizardConfig(config: IWizardConfig) {
        this.wizardService.updateConfig(config);
    }

    private initWizardUser(user: IWizardUser) {
        this.wizardService.currentUser = user;
    }

    // TODO: type is IAllNamedMeetingTypes
    private initMeetingTypes(meetingTypes: any) {
        this.wizardService.setMeetingTypes(meetingTypes);
    }

    private initSubscriptions() {
        this.pageSubscription = this.eventService.onPageChange$.subscribe((pageId: string) =>
            this.handlePageChange(pageId)
        );

        /**
          * TODO: If we want this to be more abstract i.e can handle data other than contacts,
          will need to refactor selection$ from a simple array to a dictionary that can hold multiple
          types of arrays e.g { contacts: [...], documents: [...], whatever: [...]}
          For now this only works with contacts
         */
        this.selectionSubscription = this.wizardService.selection$.subscribe((data) => {
            this.wizardService.updateOptions({ contacts: data });
            this.stepsMetaData = { ...this.stepsMetaData, selection: data };
        });
    }

    private initExistingMeeting(meeting?: IDefaultWizardOptions) {
        if (meeting) {
            this.wizardService.saveMeetingAsWizardOptions(meeting);
            this.wizardService.saveTaskId(this.wizardConfig.taskId);
        }
    }

    private buildForm() {
        const viewContainerRef = this.formHost.viewContainerRef;
        viewContainerRef.clear();

        const formOptions = this.currentPage.form;
        this.containerClasses = this.currentPage.containerClasses || [''];
        this.formItems = this.wizardBuilder.getFormComponents(formOptions);
        this.wizardBuilder.resolveFormComponentsForView(this.formItems, viewContainerRef);
    }

    private handlePageChange(pageId: string) {
        this.currentPage = this.pages[pageId];
        this.actions = this.currentPage.actions;
        this.currentStep = this.currentPage.step;
        this.buildForm();
    }

    private checkHasCalendarProviders() {
        if (this.wizardMetaData.calendar && !this.wizardMetaData.calendar.hasCalendarProviders) {
            this.errorService.setError('wizard-no-calendar-provider');
        }
    }

    private checkIsBasicallyRestricted() {
        if (!this.wizardMetaData.isAllowedApp) {
            this.errorService.setError('basic-user-restriction', {
                title: this.singleNamePipe.transform(this.wizardService.currentUser.name, 'first')
            });
            this.eventService.isNextDisabled = true;
        }
    }

    private setMetaData() {
        this.stepsMetaData.appName = this.wizardTask.appName;
        this.wizardService.updateTaskMetaData(this.wizardMetaData);
    }

    public handleCancel(event?: 'tasks') {
        this.onHandleCancel.emit(event);
    }

    public handleTrackEvent(event: WizardActionTrackingEvent) {
        this.onHandleTrackEvent.emit(event);
    }
}
